/* istanbul ignore file */

// Note that this is a .js file, not .ts, because it is imported by src/services/RedirectsManager/RedirectsManager.js, which is imported by next.config.js

// eslint-disable-next-line @typescript-eslint/no-var-requires
const { slugs } = require('./slugs')

function getBaseURL() {
  if (typeof window !== 'undefined') return window.location.origin
  if (process.env.NEXT_PUBLIC_DOMAIN) return process.env.NEXT_PUBLIC_DOMAIN

  // TODO
  // To improve the experience in preview builds, we need to create a new environment in Vercel for staging
  // and then remove NEXT_PUBLIC_DOMAIN for preview builds. Adding a staging environment in Vercel will require
  // going through and updating all the "preview" environment variables in the Vercel dashboard and adding staging.
  // Once NEXT_PUBLIC_DOMAIN is removed for preview, the VERCEL_URL fallback will work server side for preview builds.
  // The client side will still use the window.location.origin, which is the first check in this function.
  if (process.env.VERCEL_URL) return `https://${process.env.VERCEL_URL}`

  // The environment-utils file is a TS file, so we can't import it here unfortunately.
  // eslint-disable-next-line no-restricted-syntax
  if (process.env.NEXT_PUBLIC_ANGEL_ENV === 'development') return `http://localhost:${process.env.PORT || 3000}`
  return 'https://www.angel.com'
}

const paths = {
  getBaseURL,
  index: '/',
  home: '/home',
  about: {
    index: '/about',
    ourMission: '/about/our-mission',
  },
  ambassadors: {
    index: '/ambassadors',
  },
  app: {
    index: '/app',
    sketch: '/sketchapp',
  },
  assets: {
    partnerships: { index: '/assets/partnerships', cabrini: '/assets/partnerships/cabrini' },
  },
  auth: {
    accessToken: '/api/auth/accessToken',
    callback: '/api/auth/callback',
    login: '/auth/login',
    logout: '/auth/logout',
    signup: '/auth/signup',
  },
  account: {
    index: '/account',
    collectibles: '/account?t=collectibles',
    collectiblesUnbox: '/account?t=collectibles&unbox=true',
    edit: '/account?edit=true',
    guild: '/guild',
    guildMembership: '/account/guild-membership',
    guildMembershipInvite: (inviteId, claim = true) => {
      return `/account/guild-membership/invite/${inviteId}${claim ? '?claim=1' : ''}`
    },
    guildMembershipPlan: '/account/guild-membership/plan',
    guildMembershipQuote: (offerId) => `/account/guild-membership/quote/${offerId}`,
    guildMembershipSeat: (seatId) => `/account/guild-membership/seat/${seatId}`,
    myImpact: '/account?t=my-impact',
    payments: '/account?t=payments',
    settings: '/account?t=settings',
    myTickets: '/account?t=myTickets',
    notificationPreferences: '/account/notification-preferences',
  },
  audienceInterest: {
    index: '/audience-interest',
    indexSuccess: '/audience-interest?success=true',
    checkout: '/audience-interest/checkout',
    tickets: '/audience-interest/tickets',
  },
  blog: {
    index: '/blog',
    angelStudios: '/blog/angel-studios',
    castToTv:
      'https://support.angel.com/hc/en-us/articles/11102757045143-Livestreams-Early-Access-and-Casting-Issues-FAQ',
    david: `/blog/${slugs.david}`,
    jungleBeat: `/blog/${slugs.jungleBeat}`,
    ripplesOfFreedom: `/blog/${slugs.soundOfFreedom}/posts/ripples-of-freedom`,
    soundOfFreedom: `/blog/${slugs.soundOfFreedom}`,
    testament: `/blog/${slugs.testament}`,
    theChosen: `/blog/${slugs.theChosen}`,
    tuttleTwins: `/blog/${slugs.tuttleTwins}`,
    wingfeatherSaga: `/blog/${slugs.wingfeatherSaga}`,
  },
  category: {
    index: '/category',
    christianMovies: '/category/christian-movies',
    comedyTvShows: '/category/comedy-tv-shows',
    drama: '/category/drama',
    documentaries: '/category/documentaries',
    faithBased: '/category/faith-based',
    family: '/category/family',
    free: '/category/free',
    kidsShows: '/category/kids-shows',
    newReleases: '/category/new-releases',
    shows: '/category/shows',
    sports: '/category/sports',
    movies: '/category/movies',
  },
  collectibles: {
    index: '/collectibles',
    livestream: '/collectibles/livestream',
    landing: (slug) => {
      return `/collectibles/${slug}`
    },
    shop: (slug) => {
      if (!slug) return '/collectibles'
      return `/collectibles/${slug}/shop`
    },
    checkout: (slug) => {
      if (!slug) return '/collectibles'
      return `/collectibles/${slug}/checkout`
    },
    success: (slug) => {
      if (!slug) return '/collectibles'
      return `/collectibles/${slug}/checkout/success`
    },
  },
  collectiblesMarketplace: {
    index: '/collectibles',
  },
  comingSoon: {
    index: '/coming-soon',
  },
  create: {
    index: '/create',
  },
  discover: {
    index: '/discover',
    movies: '/discover/movies',
    shows: '/discover/shows',
    comedy: '/discover/comedy',
    free: '/discover/free',
    family: '/discover/family',
  },
  exhibitors: {
    index: '/exhibitors',
    cabrini: '/exhibitors/cabrini',
    sight: '/exhibitors/sight',
  },
  feedback: {
    parentalControls: '/feedback/parental-controls',
  },
  filmmakers: {
    index: '/filmmakers',
    annecy: '/annecy',
    documentaries: '/documentaries',
  },
  franchise: {
    index: '/franchise',
  },
  guild: {
    demographics: {
      complete: '/guild/demographics/complete',
    },
    index: '/guild',
    angel: '/guild/join/angel',
    angelStudios: '/guild/join/angel-studios',
    checkout: '/guild/checkout',
    checkoutSuccess: '/guild/checkout/success',
    create: {
      index: '/guild/create',
      projects: {
        index: '/guild/create/projects',
        revenue: '/guild/create/projects/revenue',
      },
      torch: '/guild/create/torch',
      status: '/guild/create/status',
    },
    drybar: '/guild/join/dry-bar-comedy',
    earlyAccess: '/guild/join/early-access',
    gift: {
      index: '/guild/gift',
      invite: (inviteId) => {
        return `/account/guild-membership/invite/${inviteId}`
      },
      invitePagePath: '/account/guild-membership/invite/:id',
      checkout: '/guild/gift/checkout',
    },
    impact: '/guild/impact',
    interval: {
      index: '/guild/interval',
      basic: '/guild/interval/basic',
      premium: '/guild/interval/premium',
    },
    investorUpdates: '/guild/investor-updates',
    join: '/guild/join',
    merchandise: '/guild/merchandise',
    monthly: '/guild/join/monthly',
    offer: {
      million: '/m/offer/1million',
    },
    pricing: '/guild/pricing',
    sale: '/guild/join/sale',
    signup: {
      createAccount: '/guild/signup/create-account',
      plan: '/guild/signup/plan',
      pricing: '/guild/signup/pricing',
      interval: {
        index: '/guild/signup/interval',
        basic: '/guild/signup/interval/basic',
        premium: '/guild/signup/interval/premium',
      },
    },
    tickets: '/guild/tickets',
    updates: '/guild/updates',
    voting: '/guild/voting',
    yearly: '/guild/join/yearly',
    projects: {
      index: '/guild/create/projects',
      admin: '/guild/create/projects/admin',
      todo: '/guild/create/projects/todo',
      submission: '/guild/create/projects/submission',
      organization: (projectId) => `/guild/create/projects/${projectId}?c=org`,
      receipts: (projectId) => `/guild/create/projects/${projectId}?c=stub`,
      team: (projectId) => `/guild/create/projects/${projectId}?c=team`,
    },
  },
  legal: {
    index: '/legal',
    devices: '/legal/devices',
    guildMembership: '/legal/guild/membership',
    guildTerms: '/legal/guild',
    marketplaceTermsOfService: '/legal/marketplace-terms-of-service',
    privacyPolicy: '/legal/privacy',
    termsOfUse: '/legal/terms-of-use',
    videoPrivacyPolicy: '/legal/video-privacy-policy',
  },
  marketing: {
    freeFunnel: '/m/free',
    aWeekAwaySneakPeek: '/m/a-week-away-sneak-peek',
    offer: '/m/offer',
    tickets: '/m/tickets',
  },
  movies: {
    index: '/movies',
    afterDeath: `/movies/${slugs.afterDeath}`,
    bonhoeffer: `/movies/${slugs.bonhoeffer}`,
    braveTheDark: `/movies/${slugs.braveTheDark}`,
    cabrini: `/movies/${slugs.cabrini}`,
    david: `/movies/${slugs.david}`,
    homestead: `/movies/${slugs.homestead}`,
    iWasAStranger: `/movies/${slugs.iWasAStranger}`,
    ruleBreakers: `/movies/${slugs.ruleBreakers}`,
    sight: `/movies/${slugs.sight}`,
    sketch: `/movies/${slugs.sketch}`,
    soloMio: `/movies/${slugs.soloMio}`,
    soundOfFreedom: `/movies/${slugs.soundOfFreedom}`,
    soundOfHopeTheStoryOfPossumTrot: `/movies/${slugs.soundOfHopeTheStoryOfPossumTrot}`,
    theLastRodeo: `/movies/${slugs.theLastRodeo}`,
    theShift: `/movies/${slugs.theShift}`,
    truthAndTreason: `/movies/${slugs.truthAndTreason}`,
    youngWashington: `/movies/${slugs.youngWashington}`,
    zeroAd: `/movies/${slugs.zeroAd}`,
  },
  music: {
    index: '/music',
  },
  notifications: {
    index: '/notifications',
  },
  partnerships: {
    index: '/partnerships',
    cabrini: '/partnerships/cabrini',
  },
  payItForward: {
    index: '/pay-it-forward',
    david: `/pay-it-forward/${slugs.david}`,
    forYourConsideration: `/pay-it-forward/${slugs.forYourConsideration}`,
    theChosen: `/pay-it-forward/${slugs.theChosen}`,
    truthAndTreason: `/pay-it-forward/${slugs.truthAndTreason}`,
    wingfeatherSaga: `/pay-it-forward/${slugs.wingfeatherSaga}`,
  },
  press: {
    index: '/press',
    release: '/press/release',
    truthAndTreason: `/press/${slugs.truthAndTreason}`,
    dryBarComedy: `/press/${slugs.dryBarComedy}`,
  },
  share: {
    content: '/share/content',
    moment: '/share/moment',
    user: '/share/u',
    story: '/share/story',
    soundOfFreedom: '/share/sound-of-freedom',
  },
  shows: {
    index: '/shows',
    aWeekAwaySeries: `/shows/${slugs.aWeekAwaySeries}`,
    dryBar: `/shows/${slugs.dryBar}`,
    homesteadSeries: `/shows/${slugs.homesteadSeries}`,
    jungleBeat: `/shows/${slugs.jungleBeat}`,
    liveNotByLies: `/shows/${slugs.liveNotByLies}`,
    riotAndTheDance: `/shows/${slugs.riotAndTheDanceSeries}`,
    testamentSeries: `/shows/${slugs.testamentSeries}`,
    theChosen: `/shows/${slugs.theChosen}`,
    theChosenSeason4: `/shows/${slugs.theChosen}/season-4`,
    tuttleTwins: `/shows/${slugs.tuttleTwins}`,
    truthAndTreasonSeries: `/shows/${slugs.truthAndTreasonSeries}`,
    wingfeatherSaga: `/shows/${slugs.wingfeatherSaga}`,
    youngDavid: `/shows/${slugs.youngDavid}`,
  },
  signup: {
    index: '/signup',
    welcome: '/welcome',
    afterDeath: '/signup/after-death',
    angelStudios: '/signup/angel-studios',
    dryBar: '/signup/dry-bar',
    freelancers: '/signup/freelancers',
    gabrielAndTheGuardians: '/signup/gabriel-and-the-guardians',
    germanKing: '/signup/the-german-king',
    possumTrot: '/signup/possum-trot',
    sight: '/signup/sight',
    soundOfHopeTheStoryOfPossumTrot: '/signup/sound-of-hope-the-story-of-possum-trot',
    testament: '/signup/testament',
    theChosen: '/signup/the-chosen',
    theChosenSeason4: '/signup/the-chosen-season-4',
    theShift: '/signup/theshift',
    truthAndConviction: '/signup/truth-and-conviction',
    truthAndTreason: '/signup/truth-and-treason',
    truthAndTreasonSeries: '/signup/truth-and-treason-series',
    tuttleTwins: '/signup/tuttle-twins',
    wingfeatherSaga: '/signup/wingfeather-saga',
    createAccount: '/signup/create-account',
  },
  submit: {
    index: '/submit',
  },
  support: {
    index: 'https://support.angel.com/hc/en-us',
    contact: 'https://support.angel.com/hc/en-us/requests/new',
  },
  preSales: {
    index: (slug) => `/pre-sales/${slug}`,
    checkout: (slug, reservationId) => {
      if (!reservationId) return `/pre-sales/${slug}`
      return `/pre-sales/${slug}/checkout/${reservationId}/preview`
    },
    complete: (slug, reservationId) => {
      if (!reservationId) return `/pre-sales/${slug}`
      return `/pre-sales/${slug}/checkout/${reservationId}/complete`
    },
    giveaway: (slug) => {
      if (!slug) return '/pre-sales'
      return `/pre-sales/${slug}/giveaway`
    },
  },
  preSalesBacker: {
    index: (slug) => {
      if (!slug) return '/pre-sales'
      return `/pre-sales/${slug}/backer`
    },
    checkout: (slug, reservationId) => {
      if (!slug) return '/pre-sales'
      if (!reservationId) return `/pre-sales/${slug}/backer`
      return `/pre-sales/${slug}/backer/checkout/${reservationId}/preview`
    },
    complete: (slug, reservationId) => {
      if (!slug) return '/pre-sales'
      if (!reservationId) return `/pre-sales/${slug}/backer`
      return `/pre-sales/${slug}/backer/checkout/${reservationId}/complete`
    },
  },
  tickets: {
    index: '/tickets',
    freeTickets: '/freetickets',
    checkout: {
      showtimes: (slug, flow = '', region = '') => {
        if (flow) return `/tickets/${slug}/${region}?flow=${flow}`
        if (region) return `/tickets/${slug}/${region}`
        return `/tickets/${slug}`
      },
      seatMap: (slug, showtimeId, reservationId) => {
        if (!showtimeId) return `/tickets/${slug}`
        if (reservationId) return `/tickets/${slug}/showtime/${showtimeId}/seats/map?reservationId=${reservationId}`
        return `/tickets/${slug}/showtime/${showtimeId}/seats/map`
      },
      selection: (slug, showtimeId, reservationId) => {
        if (!showtimeId) return `/tickets/${slug}`
        if (reservationId)
          return `/tickets/${slug}/showtime/${showtimeId}/tickets/selection?reservationId=${reservationId}`
        return `/tickets/${slug}/showtime/${showtimeId}/tickets/selection`
      },
      seatQuantitySelection: (slug, showtimeId, reservationId) => {
        if (!showtimeId) return `/tickets/${slug}`
        if (reservationId) {
          return `/tickets/${slug}/showtime/${showtimeId}/seats/quantity?reservationId=${reservationId}`
        }
        return `/tickets/${slug}/showtime/${showtimeId}/seats/quantity`
      },
      summary: (slug, reservationId) => {
        if (!reservationId) return `/tickets/${slug}`
        return `/tickets/${slug}/checkout/reservation/${reservationId}/preview`
      },
      complete: (slug, reservationId) => {
        if (!reservationId) return `/tickets/${slug}`
        return `/tickets/${slug}/checkout/reservation/${reservationId}/complete`
      },
      reservingTickets: (slug, reservationId) => {
        if (!reservationId) return `/tickets/${slug}`
        return `/tickets/${slug}/checkout/reservation/${reservationId}/reserving`
      },
      reservationChange: (slug, reservationId) => {
        if (!reservationId) return `/tickets/${slug}`
        return `/tickets/${slug}/checkout/reservation/${reservationId}/change?editReservationId=${reservationId}&flow=angel`
      },
      reservationInvite: (slug, reservationId) => {
        if (!reservationId) return `/tickets/${slug}`
        return `/tickets/${slug}/checkout/reservation/${reservationId}/invite`
      },
      reservationInviteShort: (slug, referenceNumber) => {
        if (!referenceNumber) return `an.gl/invite/${slug}`
        return `an.gl/invite/${slug}/${referenceNumber}`
      },
    },
  },
  watch: {
    index: '/watch',
    bannerUpdateExperiment: '/watch/_updated-banner',
    shared: '/watch/shared',
    aWeekAwaySeries: `/watch/${slugs.aWeekAwaySeries}`,
    axiom: `/watch/${slugs.axiom}`,
    bethlehem: `/watch/${slugs.bethlehem}`,
    betweenBorders: `/watch/${slugs.betweenBorders}`,
    beyondTheMedal: `/watch/${slugs.beyondTheMedal}`,
    bonhoeffer: `/watch/${slugs.bonhoeffer}`,
    braveTheDark: `/watch/${slugs.braveTheDark}`,
    cabrini: `/watch/${slugs.cabrini}`,
    david: `/watch/${slugs.david}`,
    dryBar: `/watch/${slugs.dryBar}`,
    dryBarToons: `/watch/${slugs.dryBarToons}`,
    fablehaven: `/watch/${slugs.fablehaven}`,
    faithOfAngels: `/watch/${slugs.faithOfAngels}`,
    family: '/watch/family',
    forKingAndCountryADrummerBoyChristmasLive: `/watch/${slugs.forKingAndCountryADrummerBoyChristmasLive}`,
    free: '/watch/free',
    freelancers: `/watch/${slugs.freelancers}`,
    gabrielAndTheGuardians: `/watch/${slugs.gabrielAndTheGuardians}`,
    hisOnlySon: `/watch/${slugs.hisOnlySon}`,
    homestead: `/watch/${slugs.homestead}`,
    homesteadFamilySurvival: `/watch/${slugs.homesteadFamilySurvival}`,
    homesteadSeries: `/watch/${slugs.homesteadSeries}`,
    homesteadSeriesEpisode1: `/watch/${slugs.homesteadSeries}/episode/dee2b65a-ccaa-437b-810d-bbd36bcfd98f`,
    homeToHarmony: `/watch/${slugs.homeToHarmony}`,
    hoovey: `/watch/${slugs.hoovey}`,
    jacob: `/watch/${slugs.jacob}`,
    jungleBeat: `/watch/${slugs.jungleBeat}`,
    kingOfKings: `/watch/${slugs.kingOfKings}`,
    lightwise: `/watch/${slugs.lightwise}`,
    liveNotByLies: `/watch/${slugs.liveNotByLies}`,
    lostOnAMountainInMaine: `/watch/${slugs.lostOnAMountainInMaine}`,
    movies: `/watch/movies`,
    quitters: `/watch/${slugs.quitters}`,
    riot: `/watch/${slugs.riot}`,
    riotAndTheDanceSeries: `/watch/${slugs.riotAndTheDanceSeries}`,
    ruleBreakers: `/watch/${slugs.ruleBreakers}`,
    shows: `/watch/shows`,
    somebodysGottaDoIt: `/watch/${slugs.somebodysGottaDoIt}`,
    somethingToStandForWithMikeRowe: `/watch/${slugs.somethingToStandForWithMikeRowe}`,
    soundOfFreedom: `/watch/${slugs.soundOfFreedom}`,
    surprisedByOxford: `/watch/${slugs.surprisedByOxford}`,
    sweetwater: `/watch/${slugs.sweetwater}`,
    testament: `/watch/${slugs.testament}`,
    the1916Project: `/watch/${slugs.the1916Project}`,
    theBlind: `/watch/${slugs.theBlind}`,
    theChosen: `/watch/${slugs.theChosen}`,
    theGermanKing: `/watch/${slugs.theGermanKing}`,
    theLastRodeo: `/watch/${slugs.theLastRodeo}`,
    theLunaticFarmer: `/watch/${slugs.theLunaticFarmer}`,
    theShift: `/watch/${slugs.theShift}`,
    torturedForChrist: `/watch/${slugs.torturedForChrist}`,
    truthAndConviction: `/watch/${slugs.truthAndConviction}`,
    truthAndTreason: `/watch/${slugs.truthAndTreason}`,
    tuttleTwins: `/watch/${slugs.tuttleTwins}`,
    wayfinders: `/watch/${slugs.wayfinders}`,
    wingfeatherSaga: `/watch/${slugs.wingfeatherSaga}`,
    youngDavid: `/watch/${slugs.youngDavid}`,
  },
  watchlist: {
    index: '/watchlist',
  },
  whatsInMyRegion: {
    index: '/whats-in-my-region',
  },
  livestream: {
    index: '/livestreams',
  },
  tvPostAuth: {
    success: '/tv-post-auth/success',
    welcome: '/tv-post-auth/welcome',
  },
  external: {
    accelerationFund: {
      index: 'https://www.angelacceleration.fund/',
    },
    appleAppStore: 'https://apps.apple.com/us/app/angel-studios/id1473663873',
    appleTv: 'https://support.angel.com/hc/en-us/articles/4622534841751-How-to-Download-the-Angel-TV-App-Apple-TV',
    bonhoefferDeclaration: {
      index: 'https://bonhoeffer.org/',
    },
    careers: 'https://jobs.lever.co/angel',
    create: 'https://portal.angel.com/issuer/info',
    distribution: 'https://www.angelstudiosdistribution.com',
    fireTv: 'https://support.angel.com/hc/en-us/articles/4637488689431-How-to-Download-the-Angel-TV-App-Fire-TV',
    giftGuild: 'https://shop.angel.com/products/angel-guild-premium-gift-membership-yearly?selling_plan=5245042919',
    googlePlayStore: 'https://link.angel.com/zGEh9m8o5Lb',
    googleTv: 'https://support.angel.com/hc/en-us/articles/4633478130839-How-to-Download-the-Angel-TV-App-Google-TV',
    vizio: 'https://support.angel.com/hc/en-us/articles/32850127938583-How-Do-I-Download-the-Angel-TV-App-Vizio',
    invest: {
      index: 'https://invest.angel.com/',
      axiom: 'https://invest.angel.com/axiom',
      david: 'https://www.thedavidmovie.com/',
      gabrielAndTheGuardians: 'https://invest.angel.com/guardians',
      germanKing: 'https://invest.angel.com/germanking',
      homestead: 'https://invest.angel.com/homestead',
      jacob: 'https://invest.angel.com/jacob',
      lastRodeo: 'https://launch.movie/lastrodeo',
      liveNotByLies: 'https://invest.angel.com/live',
      privacy: 'https://invest.angel.com/privacy',
      sketch: 'https://launch.movie/sketch',
      soundOfFreedom: 'https://invest.angel.com/freedom',
      termsOfService: 'https://invest.angel.com/terms',
      testament: 'https://invest.angel.com/testament-2',
      truthAndConviction: 'https://invest.angel.com/truth-2',
    },
    investmentFund: {
      index: 'https://fansinvest.angel.com',
      angel3: 'https://fansinvest.angel.com/angel-3',
    },
    investorRelations: {
      index: 'https://ir.angel.com/',
      secFilings: 'https://ir.angel.com/sec-filings/',
    },
    launchMovie: {
      index: 'https://launch.movie.com',
      bonhoeffer: 'https://www.launch.movie/bonhoeffer',
    },
    lgTv: 'https://support.angel.com/hc/en-us/articles/23534496032791-How-to-Download-the-Angel-TV-App-LG-TV',
    microsoft: 'https://support.angel.com/hc/articles/24474422103191-How-to-Download-the-Angel-TV-App-Xbox',
    notifyMe:
      'https://docs.google.com/forms/d/e/1FAIpQLSfg4nVLeNGbCYnzFEN2MrCWeT482EwQoNFI5LvrWEAA9e6urQ/viewform?usp=sf_link',
    possumTrotImpact: {
      index: 'https://www.possumtrotimpact.com/',
    },
    preconnect: {
      angelStudiosApi: 'https://api.angelstudios.com',
      angelStudiosImages: 'https://images.angelstudios.com',
      branch: 'https://api2.branch.io',
    },
    privacy: 'https://privacy.angel.com/',
    roku: 'https://support.angel.com/hc/en-us/articles/4467868786839-How-to-Download-the-Angel-TV-App-Roku',
    samsungTv: 'https://support.angel.com/hc/en-us/articles/23534034041111-How-to-Download-the-Angel-TV-App-Samsung-TV',
    shareVideoStory: 'https://link.angel.com/zj6bYYPnkEb',
    shop: {
      index: 'https://shop.angel.com',
      account: 'https://shop.angel.com/account',
      collections: 'https://shop.angel.com/collections',
      discount: 'https://shop.angel.com/discount',
      dryBar: 'https://shop.angel.com/pages/show-dry-bar-comedy',
      products: 'https://shop.angel.com/products',
    },
    shopTheChosen: {
      index: 'https://gifts.angel.com/',
    },
    stripe: {
      monthlyGuildPaymentLink: 'https://pay.angel.com/b/9AQ9EjcuwbYl5lC00a',
      yearlyGuildPaymentLink: 'https://pay.angel.com/b/28ocQvamo7I56pGfZ9',
    },
    zendesk: {
      privacy: 'https://angelstudiosprivacy.zendesk.com/hc/requests/new',
      contactUs: 'https://support.angel.com/hc/requests/new',
      support: 'https://support.angel.com/hc',
    },
  },
}

module.exports = {
  paths,
}
